import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ResultArtifact } from "@rnavoyager/contracts";

import { useWorkspaceFileAccess } from "./WorkspaceFileAccess";

export const SessionFileReferencesContext = createContext<{
  artifacts: readonly ResultArtifact[];
  openFile: (path: string) => void;
} | null>(null);

/** 这里只识别工作区路径；实际文件类型和访问权限由目录 API 与 Runtime 校验。 */
export function sessionRelativeFilePath(value: string): string | null {
  const path = value.startsWith("/project/") ? value.slice(9) : value;
  const parts = path.split("/");
  if (
    parts.length < 2 ||
    parts.length > 128 ||
    !["input", "result", "src", "tests"].includes(parts[0]) ||
    new TextEncoder().encode(path).length > 4096 ||
    path.includes("\\") ||
    [...path].some(
      (character) => character.charCodeAt(0) < 0x20 || character.charCodeAt(0) === 0x7f,
    ) ||
    parts.some(
      (part) => !part || part.startsWith(".") || new TextEncoder().encode(part).length > 255,
    )
  )
    return null;
  return path;
}

export function SessionFileLink({
  path,
  sessionId,
  onOpen,
  children,
}: {
  path: string;
  sessionId: string;
  onOpen?: () => void;
  children?: ReactNode;
}) {
  const references = useContext(SessionFileReferencesContext);
  const { downloadUrl } = useWorkspaceFileAccess();
  const relative = sessionRelativeFilePath(path);
  if (!relative) return <span>{children ?? path}</span>;
  const open = references ? () => references.openFile(relative) : onOpen;
  return (
    <a
      className="session-file-reference"
      href={downloadUrl(sessionId, `/project/${relative}`)}
      title={`打开 ${relative}`}
      onClick={(event) => {
        if (
          !open ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        event.preventDefault();
        open();
      }}
    >
      {children ?? relative}
    </a>
  );
}

export function SessionMarkdown({
  children,
  sessionId,
  artifacts = [],
}: {
  children: string;
  sessionId: string;
  artifacts?: readonly ResultArtifact[];
}) {
  const references = useContext(SessionFileReferencesContext);
  function resolve(value: string): string | null {
    const direct = sessionRelativeFilePath(value);
    if (direct) return direct;
    // 只凭当前会话已登记的 artifact ID 映射，不能根据同名文件猜测归属。
    try {
      const url = new URL(value);
      if (url.protocol !== "http:" && url.protocol !== "https:") return null;
      const match = /^\/artifacts\/([^/]+)$/.exec(url.pathname);
      if (!match) return null;
      const artifactId = decodeURIComponent(match[1]);
      const documentMatches = artifacts.filter((artifact) => artifact.artifactId === artifactId);
      const matches =
        documentMatches.length > 0
          ? documentMatches
          : (references?.artifacts ?? []).filter((artifact) => artifact.artifactId === artifactId);
      const paths = new Set(matches.map((artifact) => artifact.path));
      return paths.size === 1 ? sessionRelativeFilePath(matches[0].path) : null;
    } catch {
      return null;
    }
  }
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, title }) => {
          let decoded = href ?? "";
          try {
            decoded = decodeURI(decoded);
          } catch {
            /* 非法编码不参与路径识别。 */
          }
          const path = resolve(decoded);
          return path ? (
            <SessionFileLink path={path} sessionId={sessionId} />
          ) : (
            <a href={href} title={title} rel="noreferrer" target="_blank">
              {children}
            </a>
          );
        },
        code: ({ children, className }) => {
          const path = !className && typeof children === "string" ? resolve(children) : null;
          return path ? (
            <SessionFileLink path={path} sessionId={sessionId}>
              <code>{path}</code>
            </SessionFileLink>
          ) : (
            <code className={className}>{children}</code>
          );
        },
        img: ({ alt }) => (
          <span className="result-markdown-image-note">
            [外部图片未自动加载：{alt || "未命名"}]
          </span>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}
